import { ResponseError } from '@/../generated-client/runtime';

// Codes emitted by the API's ExceptionHandlingMw as a leading token in
// `exceptionMessage`, before ": ". Kept in one place so every caller that
// needs to branch on a specific tier/seat error shares the same source of truth.
export const TIER_ERROR_CODES = [
  'OPS_SEATS_FILLED',
  'RESPOND_SEATS_FILLED',
  'PLAN_OPS_REQUIRED',
  'PLAN_RESPOND_REQUIRED',
] as const;

export type TierErrorCode = (typeof TIER_ERROR_CODES)[number];

export interface ParsedApiError {
  status: number;
  code?: TierErrorCode;
  message: string;
}

const DEFAULT_RESPONSE_ERROR_MESSAGE = 'Response returned an error code';

async function readErrorBodyMessage(response: Response): Promise<string> {
  try {
    const body = await response.clone().json();
    if (body && typeof body.exceptionMessage === 'string') {
      return body.exceptionMessage;
    }
  } catch {
    // body wasn't JSON (or already consumed) — fall back to raw text below.
  }
  try {
    return await response.clone().text();
  } catch {
    return '';
  }
}

export async function parseApiError(error: unknown): Promise<ParsedApiError> {
  if (error instanceof ResponseError) {
    const status = error.response.status;
    const message = await readErrorBodyMessage(error.response);
    const code = TIER_ERROR_CODES.find(
      (candidate) => message.startsWith(`${candidate}:`) || message.includes(candidate),
    );
    return { status, code, message: message || DEFAULT_RESPONSE_ERROR_MESSAGE };
  }
  if (error instanceof Error) {
    return { status: 0, code: undefined, message: error.message };
  }
  return { status: 0, code: undefined, message: 'Unknown error' };
}
